import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/Table';
import { Typography } from 'components/typography';
import { useQuickQuoteCurrency } from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useQuickQuoteCurrency';
import { cn } from 'utils/shadcn';

import { SourceRow } from '../types';
import { isSourceIncludedInAverage } from '../utils/buildCalculationSteps';

type SourceTableProps = {
  rows: SourceRow[];
  isLowConfidenceQuote?: boolean;
  className?: string;
};

export function SourceTable({
  rows,
  isLowConfidenceQuote = false,
  className,
}: SourceTableProps) {
  const { formatAmount, formatRange } = useQuickQuoteCurrency();

  return (
    <Table className={cn('border-collapse tabular-nums', className)}>
      <TableCaption className='sr-only mt-0'>
        Buy and range by source
      </TableCaption>
      <TableHeader>
        <TableRow className='border-b border-neutral-200 hover:bg-transparent'>
          <TableHead className='h-auto max-w-[9rem] whitespace-normal px-0 py-1'>
            <Typography variant='body-xxs' weight='semibold' textColor='muted'>
              Source
            </Typography>
          </TableHead>
          <TableHead className='h-auto px-0 py-1'>
            <Typography variant='body-xxs' weight='semibold' textColor='muted'>
              Buy
            </Typography>
          </TableHead>
          <TableHead className='h-auto px-0 py-1'>
            <Typography variant='body-xxs' weight='semibold' textColor='muted'>
              Range
            </Typography>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const isExcludedFromAverage = !isSourceIncludedInAverage(
            row,
            isLowConfidenceQuote
          );

          return (
            <TableRow
              key={row.label}
              className={cn(
                'border-b border-neutral-100 last:border-b-0 hover:bg-transparent',
                isExcludedFromAverage && 'opacity-60'
              )}
            >
              <TableCell className='min-w-0 whitespace-normal p-0 py-1 align-top'>
                <Typography
                  variant='body-xxs'
                  className='break-words text-neutral-600'
                >
                  {row.label}
                  {isExcludedFromAverage ? (
                    <Typography
                      as='span'
                      variant='body-xxs'
                      className='text-error-700 italic'
                    >
                      <br />
                      unused in avg.
                    </Typography>
                  ) : null}
                </Typography>
              </TableCell>
              <TableCell className='p-0 py-1 whitespace-nowrap'>
                <Typography
                  variant='body-xxs'
                  weight='semibold'
                  className='text-neutral-700'
                >
                  {formatAmount(row.buy)}
                </Typography>
              </TableCell>
              <TableCell className='p-0 py-1 whitespace-nowrap'>
                <Typography variant='body-xxs' className='text-neutral-600'>
                  {formatRange(row.low, row.high)}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
